import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
  FormProvider,
  useForm,
  useWatch,
  UseFormReturn,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useHistory } from 'react-router-dom';
import { FiCheck, FiLock, FiMail, FiPhone, FiUser } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { AnimatePresence } from 'framer-motion';
import TagManager from 'react-gtm-module';

import {
  Button,
  DynamicForm,
  Input,
  MetaTags,
  Modal,
  // Select,
  AutoComplete,
  // MultiSelect,
  SelectCustom,
  TawkTo,
  WhatsAppButton,
} from '@components';
import { PASSWORD_EXTRA_FIELDS } from '@components/DynamicForm/constants';
import {
  useAuth,
  useEventfy,
  useEventTermsQuery,
  useFormSchemaQuery,
  useToggle,
} from '@hooks';
import {
  IntegrationLocation,
  RegisterUserRequest,
  Form,
} from '@common/types/api';
import { HistoryState } from '@services/history';
import { api } from '@services';
import { FORMS_ENDPOINT } from '@constants/endpoints';
import DropdownLang from '@pages/_layouts/private/Navigation/DropdownLang';

import { registerSchemas } from './schemas';
import * as S from '../styles';
import { SELECT_OPTIONS } from './options';
import TermsModal from './TermsModal';

/** Slug used to fetch the registration form schema */
const REGISTRATION_FORM_SLUG = 'cadastro';

/** i18n key for sign up title */
const I18N_SIGNUP_TITLE = 'sign_up.title';

type RegisterFields = RegisterUserRequest & {
  password_confirmation: string;
};

type SignUpError = AxiosError<{
  errors: Record<keyof RegisterUserRequest, string[]>;
}>;

interface TermsSectionProps {
  eventName: string;
  hasAcceptedTerms: boolean;
  toggleHasAcceptedTerms: () => void;
  toggleShouldDisplayTerms: () => void;
}

const TermsSection = ({
  eventName,
  hasAcceptedTerms,
  toggleHasAcceptedTerms,
  toggleShouldDisplayTerms,
}: TermsSectionProps) => {
  const { t } = useTranslation();

  return (
    <S.TermsWrapper>
      <p>
        {t('sign_up.termsText', { event: eventName })}
        <button type="button" onClick={toggleShouldDisplayTerms}>
          {t('sign_up.terms')}
        </button>
        .
      </p>

      <S.TermsAgreement htmlFor="terms">
        <input
          id="terms"
          name="terms"
          type="checkbox"
          checked={hasAcceptedTerms}
          onChange={toggleHasAcceptedTerms}
        />
        <span>
          <FiCheck />
        </span>
        {t('sign_up.accepted')}
      </S.TermsAgreement>
    </S.TermsWrapper>
  );
};

interface DynamicSignUpFormProps {
  form: Form;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  isLoading: boolean;
  requiresPassword: boolean;
  hasAcceptedTerms: boolean;
  children?: React.ReactNode;
}

const DynamicSignUpForm = ({
  form,
  onSubmit,
  isLoading,
  requiresPassword,
  hasAcceptedTerms,
  children,
}: DynamicSignUpFormProps) => {
  const { t } = useTranslation();

  return (
    <S.DynamicFormWrapper>
      <h2>{t(I18N_SIGNUP_TITLE)}</h2>
      <p>{t('sign_up.message')}</p>

      <DynamicForm
        form={form}
        onSubmit={onSubmit}
        isLoading={isLoading}
        extraFields={requiresPassword ? PASSWORD_EXTRA_FIELDS : []}
        submitDisabled={!hasAcceptedTerms}
      >
        {children}
      </DynamicForm>
    </S.DynamicFormWrapper>
  );
};

interface StaticSignUpFormProps {
  formMethods: UseFormReturn<RegisterUserRequest>;
  onSubmit: (fields: RegisterFields) => Promise<void>;
  isLoading: boolean;
  requiresPassword: boolean;
  hasAcceptedTerms: boolean;
  districtValue: string | undefined;
  setIsInputOtherVisibleCity: (value: boolean) => void;
}

const StaticSignUpForm = ({
  formMethods,
  onSubmit,
  isLoading,
  requiresPassword,
  hasAcceptedTerms,
  districtValue,
  setIsInputOtherVisibleCity,
}: StaticSignUpFormProps) => {
  const { t } = useTranslation();

  return (
    <FormProvider {...formMethods}>
      <S.Form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <h2>{t(I18N_SIGNUP_TITLE)}</h2>

        <p>{t('sign_up.message')}</p>

        <S.FlexWrapper $break>
          <Input
            name="name"
            placeholder={t('input.name')}
            icon={FiUser}
            autoFocus
            required
          />

          <Input
            name="phone"
            type="tel"
            mask="phone"
            placeholder={t('input.phone')}
            autoComplete="phone"
            inputMode="tel"
            required
            icon={FiPhone}
          />
        </S.FlexWrapper>
        <Input
          name="email"
          type="email"
          placeholder="E-mail"
          autoComplete="email"
          inputMode="email"
          icon={FiMail}
          required
        />
        {requiresPassword && (
          <>
            <Input
              id="new-password"
              name="password"
              type="password"
              placeholder={t('input.password')}
              autoComplete="new-password"
              icon={FiLock}
              required
            />

            <Input
              name="password_confirmation"
              type="password"
              placeholder={t('input.passwordRetry')}
              autoComplete="off"
              icon={FiLock}
              required
            />
          </>
        )}
        <S.FlexWrapper $break>
          <SelectCustom
            name="district"
            placeholder={t('input.state')}
            options={SELECT_OPTIONS}
            required
          />
          <AutoComplete
            id="city"
            name="city"
            placeholder="Cidade"
            setIsInputOtherVisible={setIsInputOtherVisibleCity}
            params={[{ name: 'uf', value: districtValue || '' }]}
            url="utils/cities"
            required
          />
        </S.FlexWrapper>

        <Button
          pill
          fullWidth
          loading={isLoading}
          disabled={!hasAcceptedTerms}
          title={t('button.register') || 'Sign up'}
          type="submit"
        />
      </S.Form>
    </FormProvider>
  );
};

// eslint-disable-next-line radar/cognitive-complexity
const SignUp = () => {
  const { t } = useTranslation();
  const event = useEventfy();
  const { signUp } = useAuth();
  const history = useHistory<HistoryState>();
  const referral = new URLSearchParams(history.location.search).get('referral');

  const termsQuery = useEventTermsQuery();
  const formSchemaQuery = useFormSchemaQuery(REGISTRATION_FORM_SLUG);

  const hasDynamicForm = !!formSchemaQuery.data;
  const requiresPassword = +event.access_type === 1;

  const formMethods = useForm<RegisterUserRequest>({
    resolver: yupResolver(registerSchemas[event.access_type]),
  });
  const { setError, reset: resetForm } = formMethods;

  const [isLoading, setIsLoading] = useState(false);

  const [hasAcceptedTerms, toggleHasAcceptedTerms] = useToggle(false);
  const [shouldDisplayTerms, toggleShouldDisplayTerms] = useToggle(false);
  const [isInputOtherVisibleCityIgnored, setIsInputOtherVisibleCity] =
    useState(false);

  const districtValue = useWatch({
    control: formMethods.control,
    name: 'district',
  });

  const handleRegistrationSuccess = () => {
    if (event.tag_google_gtm) {
      TagManager.dataLayer({ dataLayer: { event: 'eventfy_registered' } });
    }
    const message = t('sign_up.success') || 'Conta criada com sucesso!';
    toast.success(message);

    if (event.session_login) {
      history.push(history.location.state?.from || '/lobby');
    } else {
      setIsLoading(false);
      history.push('/');
    }
  };

  const handleRegistrationError = (err: unknown, useSetError = false) => {
    const fallbackMessage = t('sign_up.fail');

    if ((err as AxiosError)?.isAxiosError) {
      const errorResponse = (err as SignUpError).response;

      if (errorResponse) {
        if (useSetError) {
          Object.entries(errorResponse.data.errors).forEach(
            ([key, [message]]) => {
              const field = key as keyof RegisterUserRequest;
              setError(field, {
                type: 'required',
                message,
                shouldFocus: field === 'email',
              });
            },
          );
        }
        const firstError = Object.values(errorResponse.data.errors)[0]?.[0];
        toast.error(firstError || fallbackMessage);
      } else {
        toast.error(fallbackMessage);
      }
    } else {
      toast.error(fallbackMessage);
    }
    setIsLoading(false);
  };

  const handleSignUp = async (fields: RegisterFields) => {
    const { password_confirmation: _, ...credentials } = fields;
    try {
      setIsLoading(true);
      if (referral) {
        credentials.referral_code = referral;
      }
      await signUp(credentials);
      toggleHasAcceptedTerms();
      resetForm();
      handleRegistrationSuccess();
    } catch (err) {
      handleRegistrationError(err, true);
    }
  };

  const handleDynamicFormSubmit = async (data: Record<string, unknown>) => {
    // Remove password_confirmation before sending
    const { password_confirmation: _, ...formData } = data;

    try {
      setIsLoading(true);

      // Submit to the dynamic form endpoint - backend handles field separation
      await api.post(FORMS_ENDPOINT.SUBMIT(REGISTRATION_FORM_SLUG), formData);

      handleRegistrationSuccess();
    } catch (err) {
      handleRegistrationError(err);
      throw err;
    }
  };

  useEffect(() => {
    if (termsQuery && !termsQuery.data) {
      toggleHasAcceptedTerms();
    }
  }, [termsQuery, toggleHasAcceptedTerms]);

  return (
    <>
      <S.Container>
        <MetaTags title={t(I18N_SIGNUP_TITLE)} />

        <S.Header>
          <h1>
            <Link to="/sign-in">
              <img src={event.logo} alt={event.name} />
            </Link>
          </h1>

          <Button
            title={t('sign_in.title') || 'Login'}
            variant="outline"
            as={Link}
            to="/sign-in"
          />
        </S.Header>

        <S.Lang>
          <DropdownLang />
        </S.Lang>

        {hasDynamicForm ? (
          <>
            <DynamicSignUpForm
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              form={formSchemaQuery.data!}
              onSubmit={handleDynamicFormSubmit}
              isLoading={isLoading}
              requiresPassword={requiresPassword}
              hasAcceptedTerms={hasAcceptedTerms}
            >
              {termsQuery?.data && (
                <TermsSection
                  eventName={event.name}
                  hasAcceptedTerms={hasAcceptedTerms}
                  toggleHasAcceptedTerms={toggleHasAcceptedTerms}
                  toggleShouldDisplayTerms={toggleShouldDisplayTerms}
                />
              )}
            </DynamicSignUpForm>
          </>
        ) : (
          <>
            <StaticSignUpForm
              formMethods={formMethods}
              onSubmit={handleSignUp}
              isLoading={isLoading}
              requiresPassword={requiresPassword}
              hasAcceptedTerms={hasAcceptedTerms}
              districtValue={districtValue}
              setIsInputOtherVisibleCity={setIsInputOtherVisibleCity}
            />
            {termsQuery?.data && (
              <TermsSection
                eventName={event.name}
                hasAcceptedTerms={hasAcceptedTerms}
                toggleHasAcceptedTerms={toggleHasAcceptedTerms}
                toggleShouldDisplayTerms={toggleShouldDisplayTerms}
              />
            )}
          </>
        )}

        <TawkTo location={IntegrationLocation.REGISTER} />
        <WhatsAppButton location={IntegrationLocation.REGISTER} />
      </S.Container>

      <AnimatePresence>
        {shouldDisplayTerms && (
          <Modal handleClose={toggleShouldDisplayTerms}>
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            <TermsModal data={termsQuery.data!} />
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

export default SignUp;
